import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as L from 'leaflet';

// this is external as it's shared between the main map page and map widget on trailhead & trips pages
import renderTripsPaths from '../../utils/renderTripPaths';
import { MAPBOX_TILE_URL } from '../../utils/mapbox-config';
import ZoomHome from '../../lib/L.Control.ZoomHome.js';

// set default image paths for Leaflet
L.Icon.Default.imagePath = '../../';
L.Icon.Default.mergeOptions({
	iconRetinaUrl: 'static/assets/icons/marker-icon-2x.png',
	iconUrl: 'static/assets/icons/marker-icon.png',
	shadowUrl: 'static/assets/icons/marker-shadow.png',
});

class LeafletMap extends Component {
	constructor() {
		super();

		// map config
		// Trailhead markers span roughly [37.0, -122.5] to [38.25, -121.7].
		// maxBounds is set dynamically after init based on the current
		// viewport size so at least one marker is always visible.
		this.mapOptions = {
			attributionControl: false,
			center: [37.670506, -122.274399],
			zoom: 9,
			minZoom: 9,
			maxZoom: 18,
			zoomControl: false,
		};

		this.markerBounds = null;
		// Leaflet map object
		this.map = null;
		// TileLayer for basemap
		this.basemap = null;
		// markers representing trailhead or trips locations
		this.markers = null;
		// geojson features representing top and bottom layers of a "trip" path
		this.tripPathBottom = null;
		this.tripPathTop = null;

		// not-selected icon
		this.iconDeselected = L.icon({
			iconUrl: '/static/assets/icons/marker_orange.svg',
			iconSize: [29, 33],
		});

		// selected Icon
		this.iconSelected = L.icon({
			iconUrl: '/static/assets/icons/marker_blue.svg',
			iconSize: [29, 33],
		});

		this.handleMarkerClick = this.handleMarkerClick.bind(this);
	}

	componentDidMount() {
		// DOM exists so instantiate the Leaflet map object
		this.initMap();
	}

	componentWillReceiveProps(nextProps) {
		const { trailheads, trips, trailheadsToggled, selected, selector } =
			nextProps;

		if (trailheadsToggled && trailheads && !this.props.trailheads) {
			// upon receiving trailhead data, create the markers and add them to the map
			this.addTrailheadMarkers(trailheads);
		}

		if (!trailheadsToggled && trips && !this.props.trips) {
			// upon receiving trips data, create the markers and add them to the map
			this.addTrailheadMarkers(trips);
		}

		if (trailheadsToggled !== this.props.trailheadsToggled) {
			// if user toggled trailheads / trips in the sidebar, remove existing map markers and render new ones
			const type = trailheadsToggled ? trailheads : trips;
			this.markers.clearLayers();
			this.clearTripPaths();
			if (type) {
				this.addTrailheadMarkers(type);
			}
		}

		// Update which markers are hidden behind sidebar/legend overlays
		if (nextProps.sidebar !== this.props.sidebar) {
			if (nextProps.sidebar) {
				// Opening: delay to let sidebar animation settle
				setTimeout(() => this.updateMarkerVisibility(), 350);
			} else {
				// Closing: restore all markers immediately
				this.restoreAllMarkers();
			}
		}

		if (
			(this.markers && selected !== this.props.selected) ||
			(selected && trips && !this.props.trips) ||
			(selected && trailheads && !this.props.trailheads)
		) {
			// if markers exist and the previous selection is not the same as the current selection; OR
			// if a selection exists and trips data exists and no previous trip data existed; OR
			// if a selection exists and trailheads data exists and no previous trailheads data existed
			// Handle receiving a selected trailhead id
			this.handleMarkerSelect(selected, selector);
		}
	}

	shouldComponentUpdate() {
		// let Leaflet handle this part of the DOM, not React!
		return false;
	}

	initMap() {
		const { isMobile } = this.props;
		this.map = new L.Map('map', this.mapOptions);
		// Remove tabindex Leaflet adds to the container (BWT-25)
		this.map.getContainer().removeAttribute('tabindex');
		this.mapControl = new ZoomHome();
		this.mapControl.addTo(this.map);
		// when a user clicks the home button, clear any existing trip/trailhead selection
		this.mapControl._zoomHomeButton.onclick = () =>
			this.props.deselectTripTrailhead();
		this.basemap = L.tileLayer(MAPBOX_TILE_URL).addTo(this.map);

		// Bounding box of all trailhead markers (approximate)
		this.markerBounds = L.latLngBounds(
			[37.0, -122.5],
			[38.25, -121.7]
		);

		// Dynamically set maxBounds so at least one trailhead marker
		// is always visible. Recalculate on zoom change since the
		// viewport covers different degrees at different zoom levels.
		this.updateMaxBounds();
		this.map.on('zoomend', () => {
			this.updateMaxBounds();
			this.deferMarkerVisibility();
		});
		this.map.on('moveend', () => this.deferMarkerVisibility());

		// Draw tool removed per BWT-26 (accessibility)
	}

	panMapTo(lat, lng) {
		this.map.panTo([lat, lng], {
			duration: 0.75,
		});
	}

	updateMaxBounds() {
		if (!this.map || !this.markerBounds) return;
		const viewBounds = this.map.getBounds();
		const latPad = (viewBounds.getNorth() - viewBounds.getSouth());
		const lngPad = (viewBounds.getEast() - viewBounds.getWest());
		const padded = L.latLngBounds(
			[this.markerBounds.getSouth() - latPad, this.markerBounds.getWest() - lngPad],
			[this.markerBounds.getNorth() + latPad, this.markerBounds.getEast() + lngPad]
		);
		this.map.setMaxBounds(padded);
	}

	handleMarkerClick(event) {
		// the callback/event handler for each marker's click event
		const { target } = event;
		this.props.selectTripTrailhead(target.options.id, 'map');
	}

	clearTripPaths() {
		if (this.tripPathBottom) {
			this.map.removeLayer(this.tripPathBottom);
		}
		if (this.tripPathTop) {
			this.map.removeLayer(this.tripPathTop);
		}
	}

	handleMarkerSelect(selected, selector) {
		// actions to take when a selected trailhead id matches a marker id
		const { sidebar, trailheadsToggled } = this.props;
		const self = this;

		// first clear any existing trip path geojson layers if they were added
		this.clearTripPaths();

		this.markers.eachLayer((layer) => {
			const lat = layer._latlng.lat;
			const lng = layer._latlng.lng;

			if (layer.options.id === selected) {
				// use the selected icon style && make sure icon is above others
				layer.setIcon(self.iconSelected);
				layer.setZIndexOffset(1000);

				if (selector === 'list' && trailheadsToggled) {
					// markers representing trailheads are displayed
					// selected id came from the sidebar trailhead list,
					// make sure icon is within the map view
					self.panMapTo(layer._latlng.lat, layer._latlng.lng);
				}

				if (!trailheadsToggled) {
					// markers representing trips are displayed
					// slightly different behaviour than markers
					// set the leaflet map bounds to the trip GeoJSON and marker
					const trailData = layer.options.trailData;
					const render = renderTripsPaths(
						self.map,
						trailData,
						lat,
						lng
					);
					render();
					// store references to the trip paths so we can remove them later
					self.tripPathTop = render.tripPathTop();
					self.tripPathBottom = render.tripPathBottom();
				}

				if (selector === 'map' && !sidebar) {
					// if selected id came from a marker click && sidebar is closed
					// make sure the sidebar opens so user can see trailhead description & link
					self.props.sidebarOpen();
				}
			} else {
				// use the deselected icon style && make sure icon is below others
				layer.setIcon(self.iconDeselected);
				layer.setZIndexOffset(1);
			}
		});

		// Re-apply role, aria-label, data-lat, and visibility after setIcon
		// (setIcon replaces the DOM element, losing our attributes)
		this.markers.eachLayer((layer) => {
			const el = layer._icon;
			if (el) {
				el.setAttribute('role', 'button');
				el.setAttribute('aria-label', layer.options.title);
				el.setAttribute('data-lat', layer._latlng.lat);
			}
		});
		this.updateMarkerVisibility();
	}

	addTrailheadMarkers(data) {
		// Sort markers north-to-south so DOM/tab order matches geography
		const sorted = data.slice().sort((a, b) => {
			const latA = a.acf && a.acf.location ? +a.acf.location.lat : 0;
			const latB = b.acf && b.acf.location ? +b.acf.location.lat : 0;
			return latB - latA;
		});

		this.markers = sorted.reduce((featureGroup, val) => {
			const { acf, title, id } = val;
			const self = this;
			let trailData = null;

			if (!acf || !acf.location) {
				return featureGroup;
			}

			// if there's trip GeoJSON data, parse it and include it with the marker properties
			if (acf.edit_trail_route) {
				try {
					trailData = JSON.parse(acf.edit_trail_route);
				} catch (e) {
					throw new Error(e);
				}
			}

			const lat = +acf.location.lat;
			const lng = +acf.location.lng;
			const renderedTitle = title.rendered;

			const marker = L.marker([lat, lng], {
				icon: self.iconDeselected,
				title: renderedTitle,
				keyboard: true,
				alt: renderedTitle,
				id,
				trailData,
			}).on('click', self.handleMarkerClick);

			featureGroup.addLayer(marker);

			return featureGroup;
		}, L.featureGroup());

		this.markers.addTo(this.map);

		// Set role and aria-label on marker icons after they're on the map
		// (Leaflet 1.0.3 doesn't support these as marker options)
		this.markers.eachLayer((layer) => {
			const el = layer._icon;
			if (el) {
				el.setAttribute('role', 'button');
				el.setAttribute('aria-label', layer.options.title);
				el.setAttribute('data-lat', layer._latlng.lat);
			}
		});

		this.updateMarkerVisibility();
	}

	restoreAllMarkers() {
		if (!this.markers) return;
		this.markers.eachLayer((layer) => {
			const el = layer._icon;
			if (el) {
				el.removeAttribute('aria-hidden');
				el.setAttribute('tabindex', '0');
				el.style.opacity = '';
			}
		});
	}

	deferMarkerVisibility() {
		clearTimeout(this._visibilityTimer);
		this._visibilityTimer = setTimeout(() => this.updateMarkerVisibility(), 50);
	}

	updateMarkerVisibility() {
		if (!this.markers) return;

		// Collect bounding rects of visible overlays (sidebar, legend)
		const overlayRects = ['.Sidebar.opened', '.Legend'].reduce((rects, sel) => {
			const el = document.querySelector(sel);
			if (el && window.getComputedStyle(el).display !== 'none') {
				const r = el.getBoundingClientRect();
				if (r.width > 0 && r.height > 0) rects.push(r);
			}
			return rects;
		}, []);

		const mapContainer = this.map.getContainer();
		const mapRect = mapContainer.getBoundingClientRect();

		this.markers.eachLayer((layer) => {
			const el = layer._icon;
			if (!el) return;

			// Use lat/lng → pixel to get the marker's screen position
			// regardless of display:none state
			const pt = this.map.latLngToContainerPoint(layer.getLatLng());
			const px = mapRect.left + pt.x;
			const py = mapRect.top + pt.y;
			// Use Leaflet's stored icon size and anchor
			const icon = layer.options.icon.options;
			const iconSize = icon.iconSize || [25, 41];
			const iconAnchor = icon.iconAnchor || [12, 41];
			const left = px - iconAnchor[0];
			const right = left + iconSize[0];
			const top = py - iconAnchor[1];
			const bottom = top + iconSize[1];

			const isBehind = overlayRects.some(
				(oRect) =>
					right > oRect.left &&
					left < oRect.right &&
					bottom > oRect.top &&
					top < oRect.bottom
			);

			if (isBehind) {
				el.setAttribute('aria-hidden', 'true');
				el.setAttribute('tabindex', '-1');
				el.style.opacity = '0.2';
			} else {
				el.removeAttribute('aria-hidden');
				el.setAttribute('tabindex', '0');
				el.style.opacity = '';
			}
		});
	}

	render() {
		return <div id="map" />;
	}
}

LeafletMap.propTypes = {
	enableDistanceReadout: PropTypes.func.isRequired,
	disableDistanceReadout: PropTypes.func.isRequired,
	isMobile: PropTypes.bool.isRequired,
	selected: PropTypes.number,
	selector: PropTypes.string,
	sidebar: PropTypes.bool.isRequired,
	trailheadsToggled: PropTypes.bool.isRequired,
	trailheads: PropTypes.array,
	trips: PropTypes.array,
	selectTripTrailhead: PropTypes.func.isRequired,
	sidebarOpen: PropTypes.func.isRequired,
};

export default LeafletMap;



// WEBPACK FOOTER //
// ./map/components/LeafletMap.js